import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { authGuard, loginGuard } from '@seahorse/auth';
import { LoginComponent } from './auth/login.component';
import { NotAuthorizedComponent, NotFoundComponent } from '@seahorse/ui';
import { SuperUserAppThemeComponent } from './layout/super-user-app-theme/super-user-app-theme.component';
import { SeahorseModuleOverviewPage } from './modules/seahorse-modules/pages/seahorse-module-overview/seahorse-module-overview.page';
import { SeahorseModuleDetailsPage } from './modules/seahorse-modules/pages/seahorse-module-details/seahorse-module-details.page';
import { SeahorseModuleListComponent } from './modules/seahorse-modules/components/seahorse-module-list/seahorse-module-list.component';

const routes: Routes = [
  {
    path: 'account/login',
    component: LoginComponent,
    canActivate: [loginGuard],
  },
  {
    path: '',
    component: SuperUserAppThemeComponent,
    canActivate: [authGuard],
    children: [
      {
        path: 'dashboard',
        loadComponent: () =>
          import('./modules/dashboard/pages/dashboard/dashboard.page').then(
            (m) => m.DashboardPage
          ),
      },
      // Hidden developer reference for the SHIPM8 "next" look (design
      // handoff step 1-2). Not in the sidebar.
      {
        path: 'ui-kit',
        loadComponent: () =>
          import('./modules/ui-kit/ui-kit.page').then((m) => m.UiKitPage),
        data: { ui: { headerTitle: 'UI kit' } },
      },
      {
        path: 'customers',
        loadChildren: () =>
          import('./modules/customers/customers.routes').then(
            (m) => m.CUSTOMERS_ROUTES
          ),
      },
      {
        path: 'nautical/map',
        loadComponent: () =>
          import('./modules/nautical/map/map.page').then((m) => m.AisMapPage),
      },
      {
        path: 'nautical/ship',
        children: [
          {
            path: 'overview',
            loadComponent: () =>
              import(
                './modules/nautical/ship/pages/ship-overview/ship-overview.page'
              ).then((m) => m.NauticalShipOverviewPage),
            children: [
              {
                path: ':id',
                loadComponent: () =>
                  import(
                    './modules/nautical/ship/components/ship-summary/ship-summary.component'
                  ).then((m) => m.NauticalShipSummaryComponent),
              },
            ],
          },
          {
            path: 'details/:id',
            loadComponent: () =>
              import(
                './modules/nautical/ship/pages/ship-details/ship-details.page'
              ).then((m) => m.NauticalShipDetailsPage),
          },
        ],
      },

      {
        path: 'seahorse-modules',
        children: [
          {
            path: 'overview',
            component: SeahorseModuleOverviewPage,
            children: [
              {
                path: ':organisationId',
                component: SeahorseModuleListComponent,
              },
            ],
          },
          {
            path: 'details/:id',
            component: SeahorseModuleDetailsPage,
          },
        ],
      },
      {
        path: 'feature-releases',
        children: [
          {
            path: 'overview',
            loadComponent: () =>
              import(
                './modules/feature-releases/pages/feature-releases-overview/feature-releases-overview.page'
              ).then((m) => m.FeatureReleaseOverviewPage),
          },
        ],
      },
      {
        path: 'master-data',
        loadChildren: () =>
          import('./modules/master-data/master-data.routes').then(
            (m) => m.MASTER_DATA_ROUTES
          ),
      },
      {
        path: 'logs',
        loadChildren: () =>
          import('./modules/logs/logs.routes').then((m) => m.LOGS_ROUTES),
      },
      {
        path: 'system-messages',
        loadChildren: () =>
          import('./modules/system-messages/system-messages.routes').then(
            (m) => m.SYSTEM_MESSAGES_ROUTES
          ),
      },
      {
        path: 'invoice-flow-executions',
        loadChildren: () =>
          import(
            './modules/invoice-flow-executions/invoice-flow-executions.routes'
          ).then((m) => m.INVOICE_FLOW_EXECUTIONS_ROUTES),
      },
      {
        path: 'notifications',
        loadChildren: () =>
          import('./modules/notifications/notifications.routes').then(
            (m) => m.NOTIFICATIONS_ROUTES
          ),
      },
      // {
      //   path: 'su/contentdistribution',
      //   loadChildren: () =>
      //     import('./content-distribution/content-distribution.module').then(
      //       (m) => m.ContentDistributionModule
      //     ),
      //   canActivate: [permissionsGuard],
      //   data: { isSuperUserPage: true },
      // },
      // {
      //   path: 'su',
      //   loadChildren: () =>
      //     import('./superuser-nautical/superuser-nautical.module').then(
      //       (m) => m.SuperuserNauticalModule
      //     ),
      //   canActivate: [permissionsGuard],
      // },
      // {
      //   path: 'users',
      //   loadChildren: () =>
      //     import('./user/user.module').then((m) => m.UserModule),
      //   canActivate: [permissionsGuard],
      // },
      // {
      //   path: 'user-management',
      //   loadChildren: () =>
      //     import('./user-management/user-management.module').then(
      //       (m) => m.UserManagementModule
      //     ),
      //   canActivate: [permissionsGuard],
      // },
      // {
      //   path: 'su/service-connections',
      //   loadChildren: () =>
      //     import('./service-connections/service-connections.module').then(
      //       (m) => m.ServiceConnectionsModule
      //     ),
      //   canActivate: [permissionsGuard],
      // },
      // {
      // {
      //   path: 'su/modules',
      //   loadChildren: () =>
      //     import('./custom-modules/custom.module').then((m) => m.CustomModule),
      //   canActivate: [permissionsGuard],
      //   data: {
      //     allowedPermissions: 'superusers.superuser',
      //   },
      // },
      // {
      //   path: 'su/widgets',
      //   loadChildren: () =>
      //     import('./widgets/widgets.module').then((m) => m.WidgetsModule),
      //   canActivate: [permissionsGuard],
      //   data: {
      //     allowedPermissions: 'superusers.superuser',
      //   },
      // },
      // Service routes
      {
        path: 'not-authorized',
        component: NotAuthorizedComponent,
      },
      {
        path: 'not-found',
        component: NotFoundComponent,
        data: {
          message: 'Page not found',
        },
      },
      {
        path: '',
        redirectTo: 'dashboard',
        pathMatch: 'full',
      },
      {
        path: '**',
        redirectTo: 'dashboard',
      },
    ],
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule],
})
export class SuperUserAppRoutingModule {}
